<script setup>
import {
  computed, onBeforeUnmount, ref, watch,
} from 'vue';
import Card from '@pfsc/shared/components/card.vue';
import SkeletonLoader from '@pfsc/shared/components/skeleton-loader.vue';
import { analyticsService, BULK_GROUP, BULK_RANK } from '@pfsc/core/services/analytics.service';
import StatTile from './stat-tile.vue';

/**
 * Who buys seats in volume, so sales knows who to call.
 *
 * A bulk buyer is anyone who bought at least `min seats` of one package in a single purchase:
 * a quantity on an order line, or - for the years before orders existed - one payment settling
 * many registrations. Every figure is the server's. The list holds customer contact details, so
 * it is only ever held in this component: nothing is stored in the browser, and the CSV is built
 * from what is on screen and handed straight to the download.
 */
const props = defineProps({
  /** The dashboard's window: preset or startDate/endDate. */
  period: {
    type: Object,
    required: true,
  },
});

const DEFAULT_MIN_SEATS = 5;
const SORT_FIELDS = {
  name: { label: 'Buyer', type: 'text' },
  contactName: { label: 'Contact', type: 'text' },
  email: { label: 'Email', type: 'text' },
  phone: { label: 'Phone', type: 'text' },
  state: { label: 'State', type: 'text' },
  bulkOrders: { label: 'Bulk orders', type: 'number' },
  seats: { label: 'Seats', type: 'number' },
  revenue: { label: 'Revenue', type: 'number' },
  firstPurchase: { label: 'First bulk purchase', type: 'text' },
  lastPurchase: { label: 'Last bulk purchase', type: 'text' },
};

const allTime = ref(false);
const minSeats = ref(DEFAULT_MIN_SEATS);
const groupBy = ref(BULK_GROUP.BUYER);
const rankBy = ref(BULK_RANK.REVENUE);
const recentMonths = ref(12);
const lapsedOnly = ref(false);
const state = ref('');

const result = ref(null);
const isLoading = ref(false);
const error = ref(null);
const isDenied = ref(false);
let token = 0;
let timer = null;

const sortKey = ref(null);
const sortAscending = ref(false);

const request = computed(() => ({
  allTime: allTime.value,
  preset: props.period.preset,
  startDate: props.period.startDate,
  endDate: props.period.endDate,
  minSeats: Math.min(1000, Math.max(2, Math.floor(Number(minSeats.value)) || DEFAULT_MIN_SEATS)),
  groupBy: groupBy.value,
  rankBy: rankBy.value,
  limit: 500,
  recentMonths: recentMonths.value,
  lapsedOnly: lapsedOnly.value,
  state: /^[A-Za-z]{2}$/.test(state.value.trim()) ? state.value.trim().toUpperCase() : '',
}));

/** Fetch the list. A slower earlier request can never overwrite a newer one. */
async function load() {
  const mine = ++token;
  isLoading.value = true;
  error.value = null;
  isDenied.value = false;
  try {
    const next = await analyticsService.getBulkBuyers(request.value);
    if (mine === token) {
      result.value = next;
      sortKey.value = null;
    }
  } catch (failure) {
    if (mine === token) {
      result.value = null;
      isDenied.value = Boolean(failure?.isDenied);
      error.value = failure?.message ?? 'We could not load the bulk buyers.';
    }
  } finally {
    if (mine === token) {
      isLoading.value = false;
    }
  }
}

// Typing a seat count or a state fires a request per keystroke otherwise.
watch(request, () => {
  clearTimeout(timer);
  timer = setTimeout(load, 350);
}, { deep: true, immediate: true });

onBeforeUnmount(() => {
  clearTimeout(timer);
  token += 1;
});

const rows = computed(() => {
  const source = result.value?.buyers ?? [];
  if (!sortKey.value) {
    return source;
  }
  const { type } = SORT_FIELDS[sortKey.value];
  const direction = sortAscending.value ? 1 : -1;
  return [...source].sort((a, b) => {
    const left = a[sortKey.value];
    const right = b[sortKey.value];
    const order = type === 'number' ? left - right : String(left ?? '').localeCompare(String(right ?? ''));
    return order * direction;
  });
});

/**
 * Sort the loaded rows by a column; a second click reverses it.
 * @param {string} key Column key.
 */
function sortBy(key) {
  if (sortKey.value === key) {
    sortAscending.value = !sortAscending.value;
  } else {
    sortKey.value = key;
    sortAscending.value = SORT_FIELDS[key].type === 'text';
  }
}

/**
 * Aria sort state of a header.
 * @param {string} key Column key.
 * @returns {string}
 */
function ariaSort(key) {
  if (sortKey.value !== key) {
    return 'none';
  }
  return sortAscending.value ? 'ascending' : 'descending';
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1,
});
const number = new Intl.NumberFormat('en-US');

/**
 * Render a Y-m-d date.
 * @param {string} value Date.
 * @returns {string}
 */
function formatDate(value) {
  return value
    ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
}

/**
 * How long ago the last bulk purchase was.
 * @param {number} days Days since.
 * @returns {string}
 */
function ago(days) {
  if (days < 45) {
    return `${days} days ago`;
  }
  const months = Math.round(days / 30.4);
  return months < 24 ? `${months} months ago` : `${Math.round(months / 12)} years ago`;
}

/**
 * The packages bought, as one line.
 * @param {object} row Buyer.
 * @returns {string}
 */
function productLine(row) {
  const shown = row.products.map(product => `${product.name} (${number.format(product.seats)})`).join(', ');
  const more = row.productCount - row.products.length;
  return more > 0 ? `${shown} +${more} more` : shown;
}

/**
 * Make a value safe for a spreadsheet: quote it, and stop a leading = + - @ from running as a formula.
 * @param {any} value Cell value.
 * @returns {string}
 */
function csvCell(value) {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) {
    text = `'${text}`;
  }
  return `"${text.replace(/"/g, '""')}"`;
}

/** Download the list on screen as a CSV file. */
function exportCsv() {
  const header = [
    'Buyer',
    'Company',
    'Contact',
    'Email',
    'Phone',
    'State',
    'Bulk orders',
    'Seats',
    'Revenue',
    'Products (seats)',
    'First bulk purchase',
    'Last bulk purchase',
    'Still buying',
    'All-time bulk orders',
    'All-time seats',
    'All-time revenue',
  ];
  const lines = rows.value.map(row => [
    row.name,
    row.company,
    row.contactName,
    row.email,
    row.phone,
    row.state,
    row.bulkOrders,
    row.seats,
    row.revenue.toFixed(2),
    productLine(row),
    row.firstPurchase,
    row.lastPurchase,
    row.isRecent ? 'Yes' : 'No',
    row.lifetimeOrders,
    row.lifetimeSeats,
    row.lifetimeRevenue.toFixed(2),
  ].map(csvCell).join(','));
  const blob = new Blob([`\uFEFF${[header.map(csvCell).join(','), ...lines].join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `bulk-buyers-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const totals = computed(() => result.value?.totals ?? null);
const isTruncated = computed(() => Boolean(totals.value) && totals.value.buyers > totals.value.shown);
const subject = computed(() => (groupBy.value === BULK_GROUP.COMPANY ? 'companies' : 'buyers'));
</script>

<template>
  <div :class="$style.wrapper">
    <card :class="$style.controls">
      <div :class="$style.controlRow">
        <label :class="$style.control">
          <span :class="$style.controlLabel">Bulk means at least</span>
          <span :class="$style.inline">
            <input
              v-model.number="minSeats"
              type="number"
              min="2"
              max="1000"
              :class="[$style.input, $style.seatInput]"
            >
            seats of one package
          </span>
        </label>

        <label :class="$style.control">
          <span :class="$style.controlLabel">Group by</span>
          <select v-model="groupBy" :class="$style.select">
            <option :value="BULK_GROUP.BUYER">Person who bought</option>
            <option :value="BULK_GROUP.COMPANY">Company</option>
          </select>
        </label>

        <label :class="$style.control">
          <span :class="$style.controlLabel">Rank by</span>
          <select v-model="rankBy" :class="$style.select">
            <option :value="BULK_RANK.REVENUE">Revenue</option>
            <option :value="BULK_RANK.SEATS">Seats</option>
            <option :value="BULK_RANK.ORDERS">Bulk orders</option>
            <option :value="BULK_RANK.LAST_PURCHASE">Most recent purchase</option>
          </select>
        </label>

        <label :class="$style.control">
          <span :class="$style.controlLabel">State</span>
          <input
            v-model="state"
            type="text"
            maxlength="2"
            placeholder="Any"
            :class="[$style.input, $style.stateInput]"
          >
        </label>
      </div>

      <div :class="$style.controlRow">
        <label :class="$style.check">
          <input v-model="allTime" type="checkbox">
          All time (ignore the dates above)
        </label>
        <label :class="$style.check">
          <input v-model="lapsedOnly" type="checkbox">
          Only buyers who have gone quiet
        </label>
        <label :class="$style.control">
          <span :class="$style.controlLabel">Quiet means no bulk purchase for</span>
          <span :class="$style.inline">
            <select v-model.number="recentMonths" :class="$style.select">
              <option :value="6">6 months</option>
              <option :value="12">12 months</option>
              <option :value="24">24 months</option>
              <option :value="36">36 months</option>
            </select>
          </span>
        </label>
        <button
          v-if="rows.length > 0"
          type="button"
          :class="$style.export"
          @click="exportCsv"
        >
          Download CSV
        </button>
      </div>
    </card>

    <card v-if="error" :class="$style.message">
      <p :class="$style.messageTitle">
        {{ isDenied ? 'You do not have access to the bulk buyers list' : "We couldn't load the bulk buyers" }}
      </p>
      <p :class="$style.messageBody">
        {{ isDenied ? 'It holds customer contact details and is limited to PFS administrators.' : error }}
      </p>
      <button
        v-if="!isDenied"
        type="button"
        class="primary"
        @click="load"
      >
        Try again
      </button>
    </card>

    <card v-else-if="isLoading && !result">
      <skeleton-loader />
    </card>

    <card v-else-if="result && !result.isBuilt" :class="$style.message">
      <p :class="$style.messageTitle">
        The bulk buyers list has not been built yet
      </p>
      <p :class="$style.messageBody">
        Run <code>php artisan pfs:rollup-bulk-buyers --all</code> once; after that it refreshes every night.
      </p>
    </card>

    <div v-else-if="result" :class="[$style.results, isLoading ? $style.stale : '']">
      <div :class="$style.tiles">
        <stat-tile
          :label="groupBy === BULK_GROUP.COMPANY ? 'Companies' : 'Buyers'"
          :value="number.format(totals.buyers)"
          :hint="`Bought at least ${request.minSeats} seats of one package in one purchase`"
          tone="teal"
        />
        <stat-tile label="Bulk orders"
:value="number.format(totals.bulkOrders)"
tone="teal"
/>
        <stat-tile label="Seats"
:value="number.format(totals.seats)"
tone="orange"
/>
        <stat-tile label="Revenue"
:value="moneyCompact.format(totals.revenue)"
tone="red"
/>
        <stat-tile
          label="Gone quiet"
          :value="number.format(totals.lapsedBuyers)"
          :hint="`No bulk purchase in the last ${recentMonths} months`"
          tone="orange"
        />
      </div>

      <card v-if="rows.length === 0" :class="$style.message">
        <p :class="$style.messageTitle">
          No bulk buyers match
        </p>
        <p :class="$style.messageBody">
          Try a lower seat count, a wider date range or All time.
        </p>
      </card>

      <div v-else :class="$style.card">
        <p :class="$style.note">
          <template v-if="isTruncated">
            Showing the top {{ number.format(totals.shown) }} of {{ number.format(totals.buyers) }} {{ subject }}.
          </template>
          Seats and revenue are for the dates chosen. First and last purchase are the buyer's whole history.
          Revenue is what was paid for the bulk lines before any later refund.
          <template v-if="result.latestPurchaseAt">
            Purchases are loaded nightly; the newest one here is from {{ formatDate(result.latestPurchaseAt.slice(0, 10)) }}.
          </template>
        </p>

        <div :class="$style.scroller">
          <table :class="$style.table">
            <thead>
              <tr>
                <th
                  v-for="(field, key) in SORT_FIELDS"
                  :key="key"
                  scope="col"
                  :class="field.type === 'number' ? $style.numeric : ''"
                  :aria-sort="ariaSort(key)"
                >
                  <button type="button"
:class="$style.sort"
@click="sortBy(key)"
>
                    {{ field.label }}
                    <span v-if="sortKey === key" aria-hidden="true">{{ sortAscending ? '▲' : '▼' }}</span>
                  </button>
                </th>
                <th scope="col">
                  Bought
                </th>
                <th scope="col">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.key">
                <td :class="$style.strong">
                  {{ row.name }}
                  <span v-if="groupBy === 'buyer' && row.company" :class="$style.sub">{{ row.company }}</span>
                </td>
                <td>{{ row.contactName }}</td>
                <td>
                  <a v-if="row.email"
:href="`mailto:${row.email}`"
:class="$style.link"
>{{ row.email }}</a>
                </td>
                <td>{{ row.phone }}</td>
                <td>{{ row.state }}</td>
                <td :class="$style.numeric">
                  {{ number.format(row.bulkOrders) }}
                </td>
                <td :class="$style.numeric">
                  {{ number.format(row.seats) }}
                </td>
                <td :class="$style.numeric">
                  {{ money.format(row.revenue) }}
                </td>
                <td :class="$style.nowrap">
                  {{ formatDate(row.firstPurchase) }}
                </td>
                <td :class="$style.nowrap">
                  {{ formatDate(row.lastPurchase) }}
                  <span :class="$style.sub">{{ ago(row.daysSinceLast) }}</span>
                </td>
                <td :class="$style.products">
                  {{ productLine(row) }}
                </td>
                <td>
                  <span :class="[$style.badge, row.isRecent ? $style.badgeRecent : $style.badgeQuiet]">
                    {{ row.isRecent ? 'Buying' : 'Gone quiet' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style module>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.controls {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.controlRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px 24px;
}
.control {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.controlLabel {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-lg);
}
.input,
.select {
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 7px 10px;
  font-family: inherit;
  font-size: var(--font-size-lg);
  background: white;
}
.seatInput {
  width: 76px;
}
.stateInput {
  width: 64px;
  text-transform: uppercase;
}
.check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-lg);
  padding-bottom: 8px;
}
.export {
  margin-left: auto;
  border: 1px solid var(--border-color);
  background: white;
  border-radius: var(--border-radius-xxl);
  padding: 7px 16px;
  font-size: var(--font-size-lg);
  cursor: pointer;
}
.results {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.stale {
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}
.card {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.note {
  margin: 0;
  max-width: 100ch;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.scroller {
  overflow-x: auto;
}
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-lg);
}
.table th,
.table td {
  text-align: left;
  vertical-align: top;
  padding: 9px 10px;
  border-bottom: 1px solid var(--border-color-light);
}
.table thead th {
  color: var(--faint-color);
  font-weight: 500;
  font-size: var(--font-size-md);
  white-space: nowrap;
}
.table .numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.nowrap {
  white-space: nowrap;
}
.strong {
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.sub {
  display: block;
  font-weight: 400;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.sort {
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}
.link {
  color: var(--primary-font-dark-color);
  text-decoration: underline;
  word-break: break-all;
}
.products {
  min-width: 220px;
  color: var(--primary-font-color);
}
.badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--border-radius-xxl);
  font-size: var(--font-size-md);
  white-space: nowrap;
}
.badgeRecent {
  color: var(--green-light);
  border: 1px solid var(--green-light);
}
.badgeQuiet {
  color: var(--button-red);
  border: 1px solid var(--button-red);
}
.message {
  text-align: center;
  padding: 28px 20px;
}
.messageTitle {
  margin: 0 0 6px;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.messageBody {
  margin: 0 0 12px;
  color: var(--faint-color);
}
</style>
